import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, CardMedia, CircularProgress, Alert, useTheme, useMediaQuery } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
// Import API config to set axios defaults
import '../config/api';
import { renderAddressWithMapLink } from '../utils/addressUtils';
import { useNavigate } from 'react-router-dom';
import { Gor } from '../types';
import { getHariFromTanggal } from '../utils/dateUtils';

const CoachingClinic: React.FC = () => {
    const [list, setList] = useState<Gor[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDark = theme.palette.mode === 'dark';

    useEffect(() => {
        const fetchList = async () => {
            try {
                const res = await axios.get<Gor[]>(`/api/coaching`);
                console.log('Coaching clinic data:', res.data);
                setList(res.data);
            } catch (e) {
                setError('Gagal memuat daftar Coaching Clinic');
            } finally {
                setLoading(false);
            }
        };
        fetchList();
    }, []);

    if (loading) {
        return (
            <Container maxWidth="md">
                <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md">
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>Coaching Clinic - RaketerS</title>
            </Helmet>
            <Container maxWidth="lg">
                <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Coaching Clinic
                </Typography>
                <Grid container spacing={isMobile ? 1 : 2}>
                    {list.map((gor) => (
                        <Grid item key={gor.id} xs={6} sm={6} md={4}>
                            <Card 
                                sx={{ 
                                    cursor: gor.bisaBooking ? 'pointer' : 'default', 
                                    opacity: gor.bisaBooking ? 1 : 0.7,
                                    height: isMobile ? 'auto' : '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    background: isMobile 
                                        ? (isDark ? '#0a1626' : '#ffffff')
                                        : (isDark ? 'linear-gradient(135deg, rgba(13,71,161,0.1) 0%, rgba(21,101,192,0.05) 100%)' : undefined),
                                    border: isDark 
                                        ? '1px solid rgba(99,170,255,0.22)'
                                        : (isMobile ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.2)'),
                                    boxShadow: isMobile 
                                        ? '0 1px 3px rgba(0,0,0,0.1)'
                                        : (isDark ? '0 8px 32px rgba(13,71,161,0.3)' : undefined),
                                    borderRadius: isMobile ? 2 : 3,
                                    '&:hover': {
                                        boxShadow: isDark ? '0 12px 40px rgba(99,170,255,0.35)' : '0 2px 8px rgba(0,0,0,0.15)',
                                        borderColor: isDark ? 'rgba(99,170,255,0.35)' : undefined,
                                        transform: gor.bisaBooking && !isMobile ? 'scale(1.03)' : 'none',
                                        transition: 'all 0.3s ease'
                                    }
                                }}
                                onClick={() => gor.bisaBooking && navigate(`/coaching/${gor.id}`, { state: { gor } })}
                            >
                                <CardMedia 
                                    component="img" 
                                    height={isMobile ? 140 : 320} 
                                    image={gor.gambar} 
                                    alt={gor.nama}
                                    sx={{
                                        objectFit: 'cover',
                                        objectPosition: 'center center',
                                        width: '100%',
                                        aspectRatio: isMobile ? '1/1' : 'auto'
                                    }}
                                />
                                <CardContent sx={{ 
                                    flexGrow: isMobile ? 0 : 1, 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    p: isMobile ? 1 : undefined
                                }}>
                                    <Typography 
                                        variant={isMobile ? "body2" : "h6"} 
                                        sx={{ 
                                            fontWeight: isMobile ? 600 : 'bold', 
                                            mb: isMobile ? 0.25 : 1,
                                            fontSize: isMobile ? '0.875rem' : undefined,
                                            lineHeight: isMobile ? 1.3 : undefined,
                                            display: '-webkit-box',
                                            WebkitLineClamp: isMobile ? 2 : 'none',
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}
                                    >
                                        {gor.nama}
                                    </Typography>
                                    
                                    <Box sx={{ flexGrow: 1 }}>
                                        {!isMobile && gor.jam && (
                                            <Typography variant="body2" color="success.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                Jam: {gor.jam}
                                            </Typography>
                                        )}
                                        {isMobile && gor.biaya && gor.biaya !== '-' && (
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    color: 'error.main', 
                                                    fontWeight: 700, 
                                                    fontSize: '0.875rem',
                                                    mb: 0.25,
                                                    display: 'block'
                                                }}
                                            >
                                                Rp{parseInt(gor.biaya).toLocaleString('id-ID')}
                                            </Typography>
                                        )}
                                        {!isMobile && gor.biaya && gor.biaya !== '-' && (
                                            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700, mb: 0.5 }}>
                                                Biaya: Rp{parseInt(gor.biaya).toLocaleString('id-ID')}
                                            </Typography>
                                        )}
                                        {!isMobile && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.4 }}>
                                                {renderAddressWithMapLink(gor.alamat)}
                                            </Typography>
                                        )}
                                        {isMobile && (
                                            <Typography 
                                                variant="caption" 
                                                color="text.secondary" 
                                                sx={{ 
                                                    fontSize: '0.7rem',
                                                    mb: 0.25,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 1,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    lineHeight: 1.4
                                                }}
                                            >
                                                {renderAddressWithMapLink(gor.alamat)}
                                            </Typography>
                                        )}
                                        {isMobile && (
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600,
                                                    mb: 0.25,
                                                    color: (gor.tanggalTersedia && gor.tanggalTersedia.length > 0 && gor.tanggalTersedia[0] !== 'Coming Soon') ? 'primary.main' : 'error.main',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 1,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    lineHeight: 1.4
                                                }}
                                            >
                                                {gor.tanggalTersedia && gor.tanggalTersedia.length > 0 && gor.tanggalTersedia[0] !== 'Coming Soon' ? `${getHariFromTanggal(gor.tanggalTersedia[0])}, ${gor.tanggalTersedia[0]}` : 'Coming Soon'}
                                            </Typography>
                                        )}
                                        {!isMobile && (
                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.4 }}>
                                                Jadwal Terdekat: {gor.tanggalTersedia && gor.tanggalTersedia.length > 0 && gor.tanggalTersedia[0] !== 'Coming Soon' ? `${getHariFromTanggal(gor.tanggalTersedia[0])}, ${gor.tanggalTersedia[0]}` : 'Coming Soon'}
                                            </Typography>
                                        )}
                                        {!isMobile && (
                                            <Box>
                                                <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600, lineHeight: 1.7 }}>
                                                    Kapasitas: {gor.jumlahPemain ?? gor.jumlahBooking}/{gor.kapasitasMaksimal}
                                                </Typography>
                                                {(gor.jumlahWaitinglist ?? 0) > 0 && (
                                                    <Typography 
                                                        variant="body2" 
                                                        color="success.main" 
                                                        sx={{ 
                                                            fontSize: '0.875rem', 
                                                            lineHeight: 1.5,
                                                            mt: 0.5,
                                                            display: 'block',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        Reservasi: {gor.jumlahWaitinglist}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                        {isMobile && (
                                            <Box>
                                                <Typography 
                                                    variant="caption" 
                                                    color="warning.main"
                                                    sx={{ 
                                                        fontSize: '0.65rem',
                                                        mb: 0.25,
                                                        lineHeight: 1.4,
                                                        fontWeight: 600,
                                                        display: 'block'
                                                    }}
                                                >
                                                    Kapasitas: {gor.jumlahBooking}/{gor.kapasitasMaksimal}
                                                </Typography>
                                                {(gor.jumlahWaitinglist ?? 0) > 0 && (
                                                    <Typography 
                                                        variant="caption" 
                                                        color="success.main" 
                                                        sx={{ 
                                                            fontSize: '0.65rem',
                                                            lineHeight: 1.4,
                                                            mt: 0.25,
                                                            display: 'block',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        Reservasi: {gor.jumlahWaitinglist}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </>
    );
};

export default CoachingClinic; 